import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useParkingLot } from '../../lib/api';
import { ArrowLeft, MapPin, Car, ThumbsUp, ThumbsDown, MessageSquare, Info, Database } from 'lucide-react';
import LoadingState from '../../components/common/LoadingState';
import ErrorState from '../../components/common/ErrorState';
import { calculateFeedbackSummary } from '../../lib/utils';

const ParkingLotDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { parkingLot, isLoading, error } = useParkingLot(id || '');

    const handleBack = () => {
        navigate('/parking-lots');
    };

    if (isLoading) return <LoadingState message="Loading parking lot details..." />;
    if (error) return <ErrorState error={error.message} onRetry={() => window.location.reload()} />;
    if (!parkingLot) return <ErrorState error="Parking lot not found" onRetry={handleBack} />;

    const feedbackSummary = calculateFeedbackSummary(parkingLot.feedback || []);

    // Get all additional properties
    const allAdditionalProperties = parkingLot.additionalProperties || {};
    const additionalPropertyKeys = Object.keys(allAdditionalProperties);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button
                    onClick={handleBack}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Parking Lots
                </button>
            </div>

            {/* Main Content */}
            <div className="bg-white rounded-lg shadow-sm border">
                {/* Header Section */}
                <div className="p-6 border-b">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                                {parkingLot.name}
                            </h1>
                            <div className="flex items-center gap-2 text-gray-600 mb-4">
                                <MapPin className="h-4 w-4" />
                                <span>{parkingLot.address || 'Address not available'}</span>
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm">
                                <div className="flex items-center gap-1">
                                    <Car className="h-4 w-4 text-blue-600" />
                                    <span className="font-medium">{parkingLot.spaces || 'N/A'}</span>
                                    <span className="text-gray-600">spaces</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                                        {parkingLot.parking_type || 'Unknown'}
                                    </span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <span className="text-gray-600">Owner:</span>
                                    <span className="font-medium">{parkingLot.owner || 'Unknown'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Feedback Summary */}
                        <div className="bg-gray-50 rounded-lg p-4 min-w-[200px]">
                            <h3 className="text-sm font-medium text-gray-900 mb-3">Feedback Summary</h3>
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1">
                                        <ThumbsUp className="h-4 w-4 text-green-600" />
                                        <span className="text-sm">Positive</span>
                                    </div>
                                    <span className="text-sm font-medium">{feedbackSummary.thumbsUp}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1">
                                        <ThumbsDown className="h-4 w-4 text-red-600" />
                                        <span className="text-sm">Negative</span>
                                    </div>
                                    <span className="text-sm font-medium">{feedbackSummary.thumbsDown}</span>
                                </div>
                                {feedbackSummary.total > 0 && (
                                    <div className="pt-2 border-t">
                                        <div className="text-xs text-gray-600">
                                            {feedbackSummary.positivePercentage}% positive rating
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Details Section */}
                <div className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Basic Information */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <Info className="h-5 w-5" />
                                Basic Information
                            </h3>
                            <div className="space-y-3">
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Parking Lot ID</label>
                                    <p className="text-sm text-gray-900 mt-1 font-mono">{parkingLot.lotId}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Database ID</label>
                                    <p className="text-sm text-gray-900 mt-1 font-mono">{parkingLot._id}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Type</label>
                                    <p className="text-sm text-gray-900 mt-1 capitalize">{parkingLot.parking_type || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Owner</label>
                                    <p className="text-sm text-gray-900 mt-1">{parkingLot.owner || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Notes</label>
                                    <p className="text-sm text-gray-900 mt-1">{parkingLot.notes || 'No notes available'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Restrictions</label>
                                    <p className="text-sm text-gray-900 mt-1">{parkingLot.restriction_description || 'No restrictions'}</p>
                                </div>
                            </div>
                        </div>

                        {/* Additional Properties */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <Database className="h-5 w-5" />
                                Additional Properties
                            </h3>
                            <div className="space-y-3">
                                {additionalPropertyKeys.length > 0 ? (
                                    additionalPropertyKeys.map((key) => (
                                        <div key={key}>
                                            <label className="text-sm font-medium text-gray-700 capitalize">
                                                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                            </label>
                                            <p className="text-sm text-gray-900 mt-1">
                                                {typeof allAdditionalProperties[key] === 'object' 
                                                    ? JSON.stringify(allAdditionalProperties[key], null, 2)
                                                    : String(allAdditionalProperties[key] || 'N/A')
                                                }
                                            </p>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-sm text-gray-500">No additional properties available</p>
                                )}
                            </div>
                        </div>

                        {/* Location Information */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <MapPin className="h-5 w-5" />
                                Location
                            </h3>
                            {parkingLot.geometry && parkingLot.geometry.coordinates ? (
                                <div className="space-y-3">
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">Coordinates</label>
                                        <p className="text-sm text-gray-900 mt-1 font-mono">
                                            {parkingLot.geometry.type === 'Point' && Array.isArray(parkingLot.geometry.coordinates) && parkingLot.geometry.coordinates.length >= 2 && typeof parkingLot.geometry.coordinates[0] === 'number' && typeof parkingLot.geometry.coordinates[1] === 'number'
                                                ? `${parkingLot.geometry.coordinates[1].toFixed(6)}, ${parkingLot.geometry.coordinates[0].toFixed(6)}`
                                                : 'Complex geometry (Polygon/MultiPolygon)'}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">Geometry Type</label>
                                        <p className="text-sm text-gray-900 mt-1">{parkingLot.geometry.type || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">Raw Coordinates</label>
                                        <p className="text-sm text-gray-900 mt-1 font-mono text-xs">
                                            {JSON.stringify(parkingLot.geometry.coordinates)}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500">No location data available</p>
                            )}
                        </div>
                    </div>

                    {/* Complete Feedback Section */}
                    {parkingLot.feedback && parkingLot.feedback.length > 0 && (
                        <div className="mt-8 pt-6 border-t">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <MessageSquare className="h-5 w-5" />
                                All Feedback ({parkingLot.feedback.length} items)
                            </h3>
                            <div className="space-y-3 max-h-96 overflow-y-auto">
                                {parkingLot.feedback.map((feedback, index) => (
                                    <div key={feedback._id || index} className="bg-gray-50 rounded-lg p-4 border">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                {feedback.feedbackType === 'up' ? (
                                                    <ThumbsUp className="h-4 w-4 text-green-600" />
                                                ) : (
                                                    <ThumbsDown className="h-4 w-4 text-red-600" />
                                                )}
                                                <span className="text-sm font-medium">
                                                    {feedback.feedbackType === 'up' ? 'Positive' : 'Negative'} Feedback
                                                </span>
                                            </div>
                                            <span className="text-xs text-gray-500">
                                                {feedback.timestamp ? new Date(feedback.timestamp).toLocaleString() : 'Unknown date'}
                                            </span>
                                        </div>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                            <div>
                                                <span className="font-medium text-gray-700">User ID:</span>
                                                <span className="ml-1 text-gray-600 font-mono">{feedback.userId || 'Unknown user'}</span>
                                            </div>
                                            {feedback.searchId && (
                                                <div>
                                                    <span className="font-medium text-gray-700">Search ID:</span>
                                                    <span className="ml-1 text-gray-600 font-mono">{feedback.searchId}</span>
                                                </div>
                                            )}
                                            {feedback.spotAddress && (
                                                <div>
                                                    <span className="font-medium text-gray-700">Spot Address:</span>
                                                    <span className="ml-1 text-gray-600">{feedback.spotAddress}</span>
                                                </div>
                                            )}
                                            {feedback.spotType && (
                                                <div>
                                                    <span className="font-medium text-gray-700">Spot Type:</span>
                                                    <span className="ml-1 text-gray-600">{feedback.spotType}</span>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {feedback.comment && (
                                            <div className="mt-3 pt-3 border-t">
                                                <span className="text-xs font-medium text-gray-700">Comment:</span>
                                                <p className="text-sm text-gray-700 mt-1">{feedback.comment}</p>
                                            </div>
                                        )}
                                        
                                        <div className="mt-2 text-xs text-gray-500">
                                            Feedback ID: {feedback._id || 'No ID'}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Raw Data Section (for debugging) */}
                    <div className="mt-8 pt-6 border-t">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Raw Data (JSON)</h3>
                        <div className="bg-gray-50 rounded-lg p-4">
                            <pre className="text-xs text-gray-700 overflow-x-auto">
                                {JSON.stringify(parkingLot, null, 2)}
                            </pre>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ParkingLotDetail;
